import { useTranslation } from 'react-i18next';
import { useState, forwardRef } from 'react';
import { BlockButton } from './BlockButton';
import { MarkButton } from './MarkButton';
import { CustomMarkButton } from './CustomMarkButton';
import Tippy, { TippyProps } from '@tippyjs/react';
import { LanguageSelector } from 'app/components';
import { Modal, Dropdown } from 'app/components';
import { SearchInput } from 'app/components/ui';
import { i18n, type MessageKey } from 'shared/i18n';
import { ImportImage, ImportTable, ImportReference, ImportToC } from 'app/components/editor/import-area';
import { useNavigate } from '@tanstack/react-router';
import { useSlate } from 'slate-react';
import { MarkHeadingDropdown } from './MarkHeadingDropdown';
import { LinkButton, LinkContent } from './LinkButton';
import { BookmarkButton, BookmarkContent } from './BookmarkButton';
import { ColorButton, ColorPicker } from './ColorButton';
import { EditorModePicker } from 'app/components/editor/subtoolbar/tags/EditorModePicker';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useEditorLanguages, useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorCoarse } from 'app/state/contexts/EditorUiContext';

interface LazyTippyProps extends Omit<TippyProps, 'content' | 'children'> {
  children: React.ReactElement;
  content: React.ComponentType;
}

const LazyTippy = forwardRef<Element, LazyTippyProps>(({ children, content: ContentComponent, ...props }, ref) => {
  const [mounted, setMounted] = useState(false);

  return (
    <Tippy {...props} ref={ref} content={mounted ? <ContentComponent /> : null} onMount={() => setMounted(true)} onHidden={() => setMounted(false)}>
      {children}
    </Tippy>
  );
});

const StylingComponent = () => {
  const { t } = useTranslation('sub-toolbar');

  return (
    <>
      <div className="d-flex align-items-center">
        <MarkHeadingDropdown />
      </div>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.bold')}</p>} placement="bottom">
        <MarkButton format="bold" icon="bold" text="Bold" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.italic')}</p>} placement="bottom" theme="light">
        <MarkButton format="italic" icon="italic" text="Italic" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.underline')}</p>} placement="bottom" theme="light">
        <MarkButton format="underline" icon="underline" text="Underlined" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.align-left')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" icon="align-left" styling={{ alignment: 'align-left' }} text="Align left" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.align-center')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-center' }} icon="align-center" text="Align center" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.align-justify')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-justify' }} icon="align-justify" text="Align justify" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.align-right')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-right' }} icon="align-right" text="Align right" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.list-ol')}</p>} placement="bottom" theme="light">
        <BlockButton format="numbered-list" icon="list-ol" text="Ordered list" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.list-ul')}</p>} placement="bottom" theme="light">
        <BlockButton format="bulleted-list" icon="list-ul" text="Unordered list" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.indent')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ indent: true }} icon="indent" text="indent" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.blockquote')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ format: 'blockquote' }} icon="quote-left" text="Block quote" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.superscript')}</p>} placement="bottom" theme="light">
        <MarkButton format="superscript" icon="superscript" text="Superscript" css="toolbar-btn" />
      </Tippy>
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.subscript')}</p>} placement="bottom" theme="light">
        <MarkButton format="subscript" icon="subscript" text="Subscript" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <LazyTippy content={BookmarkContent} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <BookmarkButton format="bookmark" icon="bookmark" text="Bookmark" css="toolbar-btn" />
      </LazyTippy>
      <LazyTippy content={LinkContent} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <LinkButton format="link" icon="link" text="Link" css="toolbar-btn" />
      </LazyTippy>
      <div className="divider" />
      <Tippy content={<ColorPicker type="backgroundColor" />} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <ColorButton format="text" icon="highlighter" text="Background Color" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<ColorPicker type="color" />} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <ColorButton format="text" icon="a" text="Text Color" css="toolbar-btn" />
      </Tippy>
    </>
  );
};

interface InsertComponentProps {
  setSelected: (value: string) => void;
  selected: string | undefined;
}

const InsertComponent = ({ setSelected, selected }: InsertComponentProps) => {
  const { t } = useTranslation(['sub-toolbar', 'toolbar']);

  const items = [
    { title: t('toolbar:dropdown.insert-image'), icon: <i className="fa-regular fa-image" />, value: 'select_image' },
    { title: t('toolbar:dropdown.insert-list'), icon: <i className="fa-regular fa-table" />, value: 'select_table' },
    { title: t('toolbar:dropdown.insert-reference'), icon: <i className="fa-regular fa-link" />, value: 'select_reference' },
    { title: t('toolbar:dropdown.insert-toc'), icon: <i className="fa-regular fa-list-ol" />, value: 'select_toc' },
  ];
  const isDisabled = !useSlate().selection;

  return (
    <>
      <div className="divider" />
      <Tippy
        disabled={!isDisabled}
        delay={[500, 0]}
        content={<p className="gaby-tooltip arrow-top">{t('sub-toolbar:tooltip.insert-disabled')}</p>}
        placement="bottom"
      >
        <div className="d-flex align-items-center">
          <Dropdown sm onChange={(value) => setSelected(value as string)} items={items} value={selected} disabled={isDisabled}>
            <Dropdown.DropdownButton
              title={t('sub-toolbar:dropdown.insert.title')}
              sm
              icon={
                <div
                  className="bg-blue-300 text-blue-700 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '1.25rem', height: '1.25rem' }}
                >
                  <i className="fa-regular fa-plus" />
                </div>
              }
            />
          </Dropdown>
        </div>
      </Tippy>
    </>
  );
};

const LanguangeComponent = () => {
  const navigate = useNavigate({ from: '/documents/$documentId/sections/$sectionId' });
  const isDirty = useEditorCoarse().isDirty;
  const languages = useEditorLanguages();
  const locale = useEditorLocale();
  const onChange = (selectedLocale: string) => {
    const translation = languages.find((lang: { locale: string; isDefault?: boolean; superId?: string }) => lang.locale === selectedLocale);
    if (translation && !translation.isDefault) {
      navigate({ search: (prev) => ({ ...prev, translation: translation.superId }) });
    } else {
      navigate({ search: (prev) => ({ ...prev, translation: undefined }) });
    }
  };
  return (
    <div className="d-flex align-items-center">
      <LanguageSelector sm onChange={onChange} locale={locale} disabled={isDirty} />
    </div>
  );
};

interface ShowFormattingComponentProps {
  showFormatting: boolean;
  setShowFormatting: (value: boolean) => void;
}

const ShowFormattingComponent = ({ showFormatting, setShowFormatting }: ShowFormattingComponentProps) => {
  const { t } = useTranslation('sub-toolbar');

  return (
    <>
      <div className="divider" />
      <Tippy duration={0} content={<p className="gaby-tooltip arrow-top">{t('tooltip.show-formatting')}</p>} placement="bottom">
        <CustomMarkButton icon="paragraph" text="Show Formatting" css="toolbar-btn" selected={showFormatting} setSelected={setShowFormatting} />
      </Tippy>
    </>
  );
};

interface SubToolbarProps {
  enabled: { styling?: boolean; insert?: boolean; languange?: boolean };
  showFormatting?: boolean;
  setShowFormatting?: (value: boolean) => void;
}

export const SubToolbar = ({ enabled, showFormatting, setShowFormatting }: SubToolbarProps) => {
  const [currentModal, setCurrentModal] = useState<string | undefined>();
  const { mode, setMode } = useEditorMode();
  const [search, setSearch] = useState('');
  const openModal = (modal?: string) => {
    setSearch('');
    setCurrentModal(modal);
  };
  const closeModal = () => openModal(undefined);
  const pickerSearch = (placeholder: MessageKey) => (
    <SearchInput className="picker-search" searchInput={search} setSearchInput={setSearch} placeholder={i18n(placeholder)} />
  );
  return (
    <div className="sub-toolbar">
      <EditorModePicker currentMode={mode} onModeChange={setMode} />
      {!!enabled.styling ? <StylingComponent /> : null}
      <ShowFormattingComponent showFormatting={showFormatting ?? false} setShowFormatting={setShowFormatting ?? (() => {})} />
      {!!enabled.insert ? (
        <InsertComponent
          setSelected={(e) => {
            openModal(e);
          }}
          selected={currentModal}
        />
      ) : null}
      {!!enabled.languange ? <LanguangeComponent /> : null}
      <Modal
        show={currentModal === 'select_image'}
        onClose={closeModal}
        size={'lg'}
        titleLabelKey={'imageimport:select-image'}
        dialogClassName="import-picker-modal"
        headerActions={pickerSearch('imageimport:search-placeholder')}
      >
        <ImportImage onClose={closeModal} search={search} />
      </Modal>
      <Modal show={currentModal === 'select_table'} onClose={closeModal}>
        <ImportTable onClose={closeModal} />
      </Modal>
      <Modal show={currentModal === 'select_reference'} onClose={closeModal}>
        <ImportReference onClose={closeModal} />
      </Modal>
      <Modal titleLabelKey="toc:insert.modal-title" size="lg" show={currentModal === 'select_toc'} onClose={closeModal}>
        <ImportToC onClose={closeModal} />
      </Modal>
    </div>
  );
};
